import { Helmet } from "react-helmet-async";

type SeoProps = {
  title?: string;
  description?: string;
  url?: string;
  image?: string;
};

const SITE_NAME = "SuperKonnected";
const SITE_URL = "https://community.superkonnected.nl"; // SuperKonnected productie domein

export function Seo({
  title = "SuperKonnected - Het eerste Clean Mind Social Media platform ter wereld, voor ondernemers",
  description = "SuperKonnected is het eerste Clean Mind Social Media platform ter wereld voor ondernemers, met VibeScore voor voedzame, positieve content en echte verbinding. Verbind met gelijkgestemde ondernemers en bouw een waardevol netwerk.",
  url = SITE_URL,
  image = "/og/clean-mind-social-media.jpg"
}: SeoProps) {
  const fullTitle = title;

  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/SuperKonnected_Logo.svg`,
    slogan: "Het eerste Clean Mind Social Media platform ter wereld, voor ondernemers",
    description: description,
    sameAs: [
      "https://www.linkedin.com/company/superkonnected",
      "https://www.instagram.com/superkonnected",
      "https://superkonnected.nl"
    ],
    founder: {
      "@type": "Person",
      name: "Abdul Antonio"
    }
  };

  const webSiteJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    inLanguage: "nl-NL",
    description: description,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/search?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };

  const softwareApplicationJsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: SITE_NAME,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web Browser",
    description: description,
    url: SITE_URL,
    author: {
      "@type": "Organization",
      name: SITE_NAME
    },
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "EUR"
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      ratingCount: "150"
    }
  };

  return (
    <>
      <Helmet prioritizeSeoTags>
        <title>{fullTitle}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />
        
        {/* Hreflang for international SEO */}
        <link rel="alternate" hrefLang="nl-NL" href={`${SITE_URL}/`} />
        <link rel="alternate" hrefLang="en-US" href={`${SITE_URL}/en`} />
        <link rel="alternate" hrefLang="x-default" href={SITE_URL} />
        
        {/* Keywords */}
        <meta name="keywords" content="Clean Mind Social Media, ondernemers netwerk, VibeScore, positieve content, business community, ondernemers platform, SuperKonnected, Abdul Antonio, Business Security, netwerken" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:title" content={fullTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={`${SITE_URL}${image}`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="SuperKonnected - Clean Mind Social Media voor ondernemers" />
        <meta property="og:locale" content="nl_NL" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={fullTitle} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={`${SITE_URL}${image}`} />
        <meta name="twitter:site" content="@superkonnected" />
        <meta name="twitter:creator" content="@superkonnected" />

        {/* Additional SEO */}
        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />
        <meta name="author" content="SuperKonnected" />
        <meta name="publisher" content="SuperKonnected" />
        <meta name="copyright" content="SuperKonnected" />
        <meta name="language" content="Dutch" />
        <meta name="geo.region" content="NL" />
        <meta name="geo.placename" content="Nederland" />
        
        {/* Mobile */}
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="apple-mobile-web-app-title" content={SITE_NAME} />
        
        {/* Theme */}
        <meta name="theme-color" content="#4F6660" />
        <meta name="msapplication-TileColor" content="#4F6660" />
      </Helmet>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webSiteJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(softwareApplicationJsonLd) }} />
    </>
  );
}
